/**
 * FormTemplatePicker - Modal for selecting a form template to fill via chat.
 *
 * Shows the user's form templates first, then popular community form templates.
 * On selection, sends a chat message with the template ID so the
 * fill_pdf_form tool can process it.
 *
 * When no templates exist, shows a CTA to create one via the existing
 * "What type of template would you like to create?" modal.
 */

import { chooseTemplateTypeModalOpenAtom } from '@/atoms/formFillingAtom';
import { Button } from '@heidi/design-system/button';
import { Input } from '@heidi/design-system/input';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyP2,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerHeader,
  DialogDrawerTitle,
} from '@/components/ui/mobile-responsive-dialog-drawer';
import { useMutateImportTemplateFromGallery } from '@/hooks/mutation/useMutateImportTemplateFromGallery';
import { useQueryDocumentTemplates } from '@/hooks/query/useQueryDocumentTemplates';
import { useQueryFormTemplates } from '@/components/chat/hooks/useQueryFormTemplates';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import { TemplateCategory } from '@heidi/schema/template-gallery';
import { useTranslations } from '@heidi/runtime/i18n';
import { useSetAtom } from 'jotai';
import { AudioLines, FileText, Globe, Loader2, Plus, SearchX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

interface FormTemplatePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (templateId: string, templateName: string) => void;
  /** When true, shows a prompt to link a consultation before selecting a template */
  needsSessionLink?: boolean;
  /** Callback to trigger session linking (e.g., populate input with # to open session autocomplete) */
  onLinkSession?: () => void;
}

export const FormTemplatePicker = ({
  isOpen,
  onClose,
  onSelectTemplate,
  needsSessionLink = false,
  onLinkSession,
}: FormTemplatePickerProps) => {
  const { t } = useTranslations();
  const isMobile = useIsMobile();
  const { data: templates, isLoading: isLoadingUser } = useQueryDocumentTemplates();
  const [search, setSearch] = useState('');
  const setChooseTemplateTypeOpen = useSetAtom(chooseTemplateTypeModalOpenAtom);

  const { mutateAsync: importTemplateFromGallery, isPending: isImporting } =
    useMutateImportTemplateFromGallery();

  const { data: communityData, isLoading: isLoadingCommunity } =
    useQueryFormTemplates({ enabled: isOpen });
  const communityTemplates = communityData?.recommended_templates ?? [];

  const formTemplates = useMemo(() => {
    if (!templates) return [];
    return templates.filter(
      (tmpl) =>
        tmpl.template_category === TemplateCategory.FORM_TEMPLATE &&
        tmpl.form_fields &&
        tmpl.form_fields.length > 0
    );
  }, [templates]);

  const filteredUser = useMemo(() => {
    if (!search.trim()) return formTemplates;
    const q = search.toLowerCase();
    return formTemplates.filter((tmpl) => tmpl.name?.toLowerCase().includes(q));
  }, [formTemplates, search]);

  const filteredCommunity = useMemo(() => {
    if (!search.trim()) return communityTemplates;
    const q = search.toLowerCase();
    return communityTemplates.filter(
      (tmpl) => tmpl.title?.toLowerCase().includes(q) || tmpl.description?.toLowerCase().includes(q)
    );
  }, [communityTemplates, search]);

  const isLoading = isLoadingUser || isLoadingCommunity;
  const hasAnyResults = filteredUser.length > 0 || filteredCommunity.length > 0;
  const hasAnyTemplates = formTemplates.length > 0 || communityTemplates.length > 0;

  const handleSelect = (templateId: string, templateName: string) => {
    onSelectTemplate(templateId, templateName);
    handleClose();
  };

  const handleSelectCommunityTemplate = async (templateId: string, templateName: string) => {
    try {
      const { data } = await importTemplateFromGallery({
        input: { templateId },
        saveAnyway: true,
      });
      onSelectTemplate(data.id, templateName);
      handleClose();
    } catch {
      toast.error(t('chat.formTemplatePicker.importTemplateError'));
    }
  };

  const handleCreateNew = () => {
    onClose();
    setChooseTemplateTypeOpen(true);
  };

  const handleClose = () => {
    setSearch('');
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) handleClose();
  };

  const renderTemplateList = () => {
    if (needsSessionLink) return null;

    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="size-5 animate-spin text-text-tertiary" />
        </div>
      );
    }

    if (!hasAnyTemplates) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
          <FileText className="size-10 text-text-tertiary" />
          <div className="flex flex-col gap-1">
            <TypographyP2Bold className="text-text-primary">
              {t('chat.formTemplatePicker.noFormTemplatesYet')}
            </TypographyP2Bold>
            <TypographyCaption className="text-text-secondary">
              {t('chat.formTemplatePicker.noFormTemplatesDescription')}
            </TypographyCaption>
          </div>
          <Button onClick={handleCreateNew} size="sm" className="mt-1">
            <Plus className="mr-1.5 size-4" />
            {t('chat.formTemplatePicker.createNewTemplate')}
          </Button>
        </div>
      );
    }

    if (!hasAnyResults) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
          <SearchX className="size-8 text-text-tertiary" />
          <TypographyP2 className="text-text-secondary">
            {t('chat.formTemplatePicker.noFormsMatchSearch')}
          </TypographyP2>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-1">
        {filteredUser.length > 0 && (
          <>
            <TypographyCaptionBold className="px-3 pb-1 pt-2 text-text-tertiary">
              {t('chat.formTemplatePicker.myForms')}
            </TypographyCaptionBold>
            {filteredUser.map((template) => (
              <Button
                key={template.id} size="lg"
                variant="ghost"
                onClick={() => handleSelect(
                  template.id,
                  template.name || t('chat.formTemplatePicker.defaultFormName')
                )}
                className="flex h-auto w-full items-center justify-start gap-3 rounded-lg px-3 py-2.5 text-left"
              >
                <FileText className="size-4 shrink-0 text-text-tertiary" />
                <div className="min-w-0 flex-1">
                  <TypographyP2Bold className="truncate text-text-primary">
                    {template.name}
                  </TypographyP2Bold>
                  {template.usage > 0 && (
                    <TypographyCaption className="text-text-tertiary">
                      {t('chat.formTemplatePicker.usedCount', { count: template.usage })}
                    </TypographyCaption>
                  )}
                </div>
              </Button>
            ))}
          </>
        )}

        {filteredCommunity.length > 0 && (
          <>
            <TypographyCaptionBold className="px-3 pb-1 pt-3 text-text-tertiary">
              {t('chat.formTemplatePicker.communityForms')}
            </TypographyCaptionBold>
            {filteredCommunity.map((template) => (
              <Button
                key={template.template_id} size="lg"
                variant="ghost"
                disabled={isImporting}
                onClick={() => void handleSelectCommunityTemplate(
                  template.template_id,
                  template.title || t('chat.formTemplatePicker.defaultFormName')
                )}
                className="flex h-auto w-full items-center justify-start gap-3 rounded-lg px-3 py-2.5 text-left"
              >
                {isImporting
                  ? <Loader2 className="size-4 shrink-0 animate-spin text-text-tertiary" />
                  : <Globe className="size-4 shrink-0 text-text-tertiary" />
                }
                <div className="min-w-0 flex-1">
                  <TypographyP2Bold className="truncate text-text-primary">
                    {template.title}
                  </TypographyP2Bold>
                  <TypographyCaption className="truncate text-text-tertiary">
                    {[
                      template.specialty,
                      template.imports_count > 0
                        ? t('chat.formTemplatePicker.usesCount', { count: template.imports_count })
                        : null,
                    ].filter(Boolean).join(' · ')}
                  </TypographyCaption>
                </div>
              </Button>
            ))}
          </>
        )}
      </div>
    );
  };

  return (
    <DialogDrawer open={isOpen} onOpenChange={handleOpenChange}>
      <DialogDrawerContent>
        <DialogDrawerHeader>
          <DialogDrawerTitle>
            {t('chat.formTemplatePicker.title')}
          </DialogDrawerTitle>
        </DialogDrawerHeader>

        <div className="flex flex-col gap-2">
          {needsSessionLink && (
            <div className="flex flex-col items-center border gap-3 rounded-lg p-4 text-center">
              <AudioLines className="size-8" />
              <div className="flex flex-col gap-1">
                <TypographyP2Bold className="text-text-primary">
                  {t('chat.formTemplatePicker.linkConsultationFirst')}
                </TypographyP2Bold>
                <TypographyCaption className="text-text-secondary">
                  {t('chat.formTemplatePicker.linkConsultationDescription')}
                </TypographyCaption>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  handleClose();
                  onLinkSession?.();
                }}
              >
                <AudioLines className="mr-1.5 size-4" />
                {t('chat.formTemplatePicker.linkConsultation')}
              </Button>
            </div>
          )}

          {!isLoading && hasAnyTemplates && !needsSessionLink && (
            <div className="px-3 sm:px-0">
            <Input
              type="text"
              placeholder={t('chat.formTemplatePicker.searchForms')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus={!isMobile}
              
            />
            </div>
          )}

          <ScrollArea className="max-h-[40vh] overflow-y-auto">
            {renderTemplateList()}
          </ScrollArea>

          <div className={cn(
            'flex gap-2 items-center', 
            needsSessionLink || !hasAnyTemplates ? 'justify-end':  'justify-between'
          )}>
            {!needsSessionLink && hasAnyTemplates && <Button variant="ghost" size="sm" onClick={handleCreateNew}>
                <Plus className="mr-1.5 size-4" />
                {t('chat.formTemplatePicker.newTemplate')}
              </Button>
            }
            <Button size="lg" variant="ghost" onClick={handleClose}>
              {t('chat.formTemplatePicker.cancel')}
            </Button>
          </div>
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
};
